import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Edit, Trash2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';

const UNITS = ['mq', 'ml', 'ore', 'pz', 'kg', 'lt', 'mc', 'cad', 'a corpo', '%'];
const CATEGORIES = ['lavorazione', 'fornitura', 'manodopera', 'noleggio', 'trasporto', 'sicurezza', 'accessorio'];

const emptyForm = {
  code: '', description: '', category: 'lavorazione', subcategory: '', unit: 'mq',
  price_type: 'standard', percentage_value: 0, price_supply: 0, price_work: 0, notes: ''
};

export default function PriceListsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('__all__');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    try {
      const params = { page, per_page: 50 };
      if (search) params.search = search;
      if (filterCat !== '__all__') params.category = filterCat;
      const { data } = await api.get('/price-lists', { params });
      if (data.items) {
        setItems(data.items);
        setTotalPages(data.total_pages || 1);
        setTotal(data.total || 0);
      } else {
        setItems(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
        setTotalPages(1);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [search, filterCat, page]);

  useEffect(() => { setPage(1); }, [search, filterCat]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...emptyForm }); setEditingId(null); setShowForm(true); };
  const openEdit = (item) => {
    setForm({
      code: item.code || '', description: item.description || '', category: item.category || 'lavorazione',
      subcategory: item.subcategory || '', unit: item.unit || 'mq', price_type: item.price_type || 'standard',
      percentage_value: item.percentage_value || 0, price_supply: item.price_supply || 0,
      price_work: item.price_work || 0, notes: item.notes || ''
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/price-lists/${editingId}`, form);
      } else {
        await api.post('/price-lists', form);
      }
      setShowForm(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa voce del prezziario?')) return;
    try { await api.delete(`/price-lists/${id}`); load(); }
    catch (err) { alert(err.response?.data?.detail || 'Errore eliminazione'); }
  };

  const fmt = (v) => parseFloat(v || 0).toFixed(2);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="price-lists-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Prezziario</h1>
        <Button onClick={openCreate} data-testid="add-price-btn"><Plus size={16} className="mr-1" /> Nuova Voce</Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cerca per codice o descrizione..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="price-search" />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte le categorie</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-500 self-center">{total} voci</span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Codice</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead>Cat.</TableHead>
                <TableHead>U.M.</TableHead>
                <TableHead className="text-right">Fornitura</TableHead>
                <TableHead className="text-right">Lavorazione</TableHead>
                <TableHead className="text-right">Totale</TableHead>
                <TableHead className="w-24">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Nessuna voce trovata</TableCell></TableRow>
              ) : items.map(item => (
                <TableRow key={item.id} className="hover:bg-slate-50" data-testid={`price-row-${item.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{item.code}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{item.description}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{item.category}</Badge></TableCell>
                  <TableCell className="text-sm">{item.unit}</TableCell>
                  <TableCell className="text-sm text-right">{item.price_type === 'standard' ? `${fmt(item.price_supply)} EUR` : '-'}</TableCell>
                  <TableCell className="text-sm text-right">{item.price_type === 'standard' ? `${fmt(item.price_work)} EUR` : '-'}</TableCell>
                  <TableCell className="text-sm text-right font-medium">
                    {item.price_type === 'standard' ? `${fmt(item.price_total)} EUR` : <span className="text-amber-600">{item.percentage_value > 0 ? '+' : ''}{item.percentage_value}%</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="p-1 h-auto text-blue-600"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-slate-500">Pagina {page} di {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={16} /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={16} /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Voce' : 'Nuova Voce Prezziario'}</DialogTitle>
            <DialogDescription>Compila i dati della voce</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-1.5"><Label>Codice *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={!!editingId} data-testid="price-code" /></div>
            <div className="space-y-1.5">
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Descrizione *</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="price-description" /></div>
            <div className="space-y-1.5">
              <Label>Unita di misura</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo prezzo</Label>
              <Select value={form.price_type} onValueChange={(v) => setForm({ ...form, price_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="percentage_increase">Maggiorazione %</SelectItem>
                  <SelectItem value="percentage_discount">Sconto %</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.price_type === 'standard' ? (
              <>
                <div className="space-y-1.5"><Label>Prezzo Fornitura (EUR)</Label><Input type="number" step="0.01" value={form.price_supply} onChange={(e) => setForm({ ...form, price_supply: parseFloat(e.target.value) || 0 })} /></div>
                <div className="space-y-1.5"><Label>Prezzo Lavorazione (EUR)</Label><Input type="number" step="0.01" value={form.price_work} onChange={(e) => setForm({ ...form, price_work: parseFloat(e.target.value) || 0 })} /></div>
              </>
            ) : (
              <div className="col-span-2 space-y-1.5"><Label>Valore Percentuale</Label><Input type="number" step="0.1" value={form.percentage_value} onChange={(e) => setForm({ ...form, percentage_value: parseFloat(e.target.value) || 0 })} /></div>
            )}
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.code || !form.description} data-testid="price-save-btn">{editingId ? 'Aggiorna' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
